/**
 * useResponsiveCanvas hook
 *
 * Manages responsive canvas sizing for different screen sizes.
 * Stretches to fill available space.
 */

import { useEffect, useRef, useState, useCallback } from 'react';

export interface CanvasDimensions {
  width: number;
  height: number;
  scale: number;
}

export interface UseResponsiveCanvasReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  dimensions: CanvasDimensions;
  isMobile: boolean;
  isTablet: boolean;
  orientation: 'portrait' | 'landscape';
  updateDimensions: () => void;
}

// Base game dimensions (desktop reference)
const BASE_WIDTH = 900;
const BASE_HEIGHT = 600;

// Breakpoints
const MOBILE_BREAKPOINT = 640;
const TABLET_BREAKPOINT = 1024;

export function useResponsiveCanvas(): UseResponsiveCanvasReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const [dimensions, setDimensions] = useState<CanvasDimensions>({
    width: BASE_WIDTH,
    height: BASE_HEIGHT,
    scale: 1,
  });
  
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(
    window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
  );

  const isMobile = windowWidth <= MOBILE_BREAKPOINT;
  const isTablet = windowWidth > MOBILE_BREAKPOINT && windowWidth <= TABLET_BREAKPOINT;

  // Calculate responsive dimensions
  const updateDimensions = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const width = containerRect.width;
    const height = containerRect.height;
    const scale = width / BASE_WIDTH;

    setDimensions({ width, height, scale });
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setOrientation(
        window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
      );
      updateDimensions();
    };

    // Use ResizeObserver for container changes
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', handleResize);
    
    // Initial calculation
    updateDimensions();

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [updateDimensions]);

  // Handle orientation change
  useEffect(() => {
    const handleOrientationChange = () => {
      setTimeout(() => {
        setOrientation(
          window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'
        );
        updateDimensions();
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, [updateDimensions]);

  // Update canvas size when dimensions change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
  }, [dimensions]);

  return {
    containerRef,
    canvasRef,
    dimensions,
    isMobile,
    isTablet,
    orientation,
    updateDimensions,
  };
}
